<script>
  import Header from "lib/components/Header.svelte";
  import { recordingStore, withRecordedSubtasks } from "@stores/recordingStore.js";
  import {
    Accordion,
    Tooltip,
    InfoBlock,
    SectionWrapper,
    Button
  } from "paxray-components-svelte";
  import ContentSwitcher from "@components/ContentSwitcher.svelte";
  import LineChartNeu from "@components/LineChartNeu.svelte";
  import LFilters from "../components/LFilters.svelte";
  import { PROJECT_STAGES } from "@utils/stagesAndSteps.js";
  import { Loading, Search } from "carbon-components-svelte";
  import { getContext, onMount } from "svelte";
  import SideKPI from "@components/sideKPI.svelte";
  import SearchableList from "@components/SearchableList.svelte";
  import { fetchWrapper } from "lib/util/fetchWrapper.js";
  import ProjectStepActions from "../components/ProjectStepActions.svelte";
  import DataTableBlack from "@components/DataTableBlack.svelte";
  import EmptyStateText from "../components/EmptyStateText.svelte";
  import SpotlightGuide from "@components/common/SpotlightGuide.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";
  import { SORT_ARROW_ICON, SORTED_ARROW2_ICON } from '@utils/icons.js';

  export let idproject;

  const session = getContext("session");
  const step = PROJECT_STAGES.TASK_REGISTRATION.steps.REG_OVERVIEW;
  const stage = PROJECT_STAGES.TASK_REGISTRATION.id;
  let sufficientNumberOfRegistrations = 10;

  function formatNewestDate(date) {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${day}.${month}.${year}`;
  }

  function getDateSortValue(value) {
    if (!value) return -Infinity;
    const [day, month, year] = String(value).split(".").map(Number);
    if (!day || !month || !year) return -Infinity;
    return new Date(2000 + year, month - 1, day).getTime();
  }

  function sortDateStrings(a, b) {
    return getDateSortValue(a) - getDateSortValue(b);
  }

  const tableHeaders = [
    { key: "team", value: "Team" },
    { key: "l1", value: "Process (L1)" },
    { key: "l2", value: "Subprocess (L2)" },
    { key: "l3", value: "Task (L3)" },
    { key: "l4", value: "Subtask (L4)" },
    { key: "nRecordings", value: "Registrations" },
    { key: "newestRecording", value: "Last Registration", sort: sortDateStrings },
  ];

  let selectAssignedOptions = [
    { key: "enough_registrations", value: "10+ registrations" },
    { key: "limited_registrations", value: "1-9 registrations" },
    { key: "no_registrations", value: "No registrations" },
  ];
  let selectedAssignedOption = "";
  let weeklyChartData;
  let tablePagination = { page: 1, pageSize: 10 };
  let tableSearchTerm = "";
  let selectedProcess = "";
  let selectedSubprocess = "";
  let selectedTeam = "";
  let selectedTask = "";
  let selectedSubtask = "";

  let selectMode = 0;
  let totalRegistrations;
  let last7DaysRegistrations;
  let lastRegistration = "";
  let unassignedRegistrations;
  let selectedCoverageLevel = 0;
  // Bis die Registrierungen dieses Projekts da sind, Ladezustand statt 0 zeigen.
  $: loadingData = $recordingStore._loadedIdproject !== idproject;
  let loadedDataBeforeChart = true;
  let listOfCoverage = [];
  let displayedListOfCoverage = [];
  let teamOverview = [];
  let displayedTeamOverview = [];
  let teams = [];
  let filterTeamOptions = [];
  $: countLegend = [
    { className: "count_none", label: "No registrations" },
    {
      className: "count_limited",
      label: `1 to ${sufficientNumberOfRegistrations - 1} registrations`,
    },
    {
      className: "count_enough",
      label: `${sufficientNumberOfRegistrations} or more, goal reached`,
    },
  ];

  // L4 zeigt schon die Tabelle, Coverage fasst nur die Ebenen darüber zusammen.
  let levelNames = ["L3", "L2", "L1"];
  let loadingChart = true;
  let coverageSorterByNumber = "asc"; //"", "desc, "asc"
  let coverageSorterByName = ""; //"", "desc, "asc"
  let teamSorterByNumber = "asc"; //"", "desc, "asc"
  let teamSorterByName = ""; //"", "desc, "asc"
  let dataInflowOpen = true;
  let coverageOpen = false;
  let teamOverviewOpen = false;
  let dashboardPanelsBeforeGuide;
  let dashboardGuideActive = false;
  let showDashboardGuide = false;
  let dashboardGuideStep = 0;
  let dashboardGuideSteps = [];

  onMount(() => registerViewTour(startDashboardGuide));

  $: if (dashboardGuideActive) {
    if (showDashboardGuide) {
      const panel = dashboardGuideSteps[dashboardGuideStep]?.panel;
      dataInflowOpen = panel === "inflow";
      coverageOpen = panel === "coverage";
      teamOverviewOpen = panel === "teams";
    } else {
      ({ dataInflowOpen, coverageOpen, teamOverviewOpen } =
        dashboardPanelsBeforeGuide);
      dashboardGuideActive = false;
    }
  }

  function startDashboardGuide() {
    if (
      dashboardGuideActive ||
      loadingData
    )
      return;
    dashboardPanelsBeforeGuide = {
      dataInflowOpen,
      coverageOpen,
      teamOverviewOpen,
    };
    dashboardGuideSteps = [
      {
        target: '[data-guide="dashboard-inflow"]',
        title: "Review registration progress",
        panel: "inflow",
        text: `Use this dashboard to see which subtasks your teams have registered and where more examples are needed. Each subtask (L4) needs at least ${sufficientNumberOfRegistrations} registrations. “Progress” shows how many subtasks reached that goal, all registrations, registrations from the last seven days and registrations that are not linked to a subtask yet.`,
      },
      {
        target: '[data-guide="dashboard-filters"]',
        title: "Focus on the relevant work",
        text: "Filter by team, process, subprocess or task. Use Registration status to find subtasks with no registrations, 1 to 9 registrations or 10 or more. Use search to find a specific subtask.",
      },
      {
        target: '[data-guide="dashboard-table"]',
        title: "Read the registration overview",
        text: `Each row is one subtask (L4) with its team. “Registrations” is red with no registrations, yellow with 1 to ${sufficientNumberOfRegistrations - 1} and green with ${sufficientNumberOfRegistrations} or more. “Last Registration” shows the date of the latest one. Sort the columns to find gaps or check recent activity.`,
      },
      {
        target: '[data-guide="dashboard-teams"]',
        title: "Review team coverage",
        panel: "teams",
        text: "Open “Team Overview” to compare registrations per team within the selected filters. Sort by number to find teams that may need support and click a team to show only its subtasks. Then ask the teams with red or yellow rows to register more examples.",
      },
      {
        target: '[data-guide="dashboard-coverage"]',
        title: "Find the weakest areas",
        panel: "coverage",
        text: `Open “Coverage” to see for each task, subprocess or process how many of its subtasks have ${sufficientNumberOfRegistrations} or more registrations. Switch between L3, L2 and L1. The weakest areas are listed first.`,
      },
    ].filter(({ target }) => {
      const element = document.querySelector(target);
      return (
        element &&
        getComputedStyle(element).visibility === "visible" &&
        element.getBoundingClientRect().width > 0 &&
        element.getBoundingClientRect().height > 0
      );
    });
    dashboardGuideStep = 0;
    showDashboardGuide = dashboardGuideSteps.length > 0;
    dashboardGuideActive = true;
  }

  $: breadcrumbsClickable = session?.isValid === true;

  let lFiltersRef;
  $: hasActiveFilters = Boolean(
    tableSearchTerm ||
      selectedAssignedOption ||
      selectedTeam ||
      selectedProcess ||
      selectedSubprocess ||
      selectedTask,
  );

  function resetAllFilters() {
    tableSearchTerm = "";
    lFiltersRef?.resetFilters();
  }

  $: ({
    processes,
    listOfSubprocesses,
    listOfTasks,
    listOfSubtasks: storeSubtasks,
    listOfRecordings: rawListOfRecordings,
  } = $recordingStore);

  $: listOfSubtasks = withRecordedSubtasks(storeSubtasks, rawListOfRecordings);

  $: listOfRecordings = (rawListOfRecordings || []).map((recording) => ({
    ...recording,
    recorderid: recording.recorderid ?? recording.recording_id,
    teamid: recording.teamid ?? recording.team_id,
    starttimeutc: recording.starttimeutc ?? recording.start_time,
    endtimeutc: recording.endtimeutc ?? recording.end_time,
  }));

  // Die Abhängigkeiten stehen vorne, damit Svelte bei jeder Änderung neu filtert.
  $: filteredListofRecordings =
    (listOfRecordings,
    listOfSubtasks,
    listOfTasks,
    listOfSubprocesses,
    processes,
    selectedProcess,
    selectedSubprocess,
    selectedTask,
    selectedSubtask,
    selectedTeam,
    setFilteredListofRecordings());
  // Team Overview ohne Team-Filter, damit nach einem Klick alle Teams sichtbar bleiben.
  $: recordingsForTeamOverview =
    (listOfRecordings,
    listOfSubtasks,
    listOfTasks,
    listOfSubprocesses,
    processes,
    selectedProcess,
    selectedSubprocess,
    selectedTask,
    selectedSubtask,
    setFilteredListofRecordings({ ignoreTeam: true }));

  $: {
    listOfSubtasks;
    getDataInflowKPIs(filteredListofRecordings);
  }

  // Registrierungen je Subtask (L4) im Project Framework, nach Filtern.
  $: subtaskCounts =
    (filteredListofRecordings,
    listOfSubtasks,
    selectedProcess,
    selectedSubprocess,
    selectedTask,
    selectedTeam,
    filterTeamOptions,
    getSubtaskCounts());
  $: subtaskCoverage = Array.from(subtaskCounts.values());
  $: subtasksWithEnough = subtaskCoverage.filter(
    (item) => item.count >= sufficientNumberOfRegistrations,
  ).length;
  // Coverage fasst pro Task, Subprocess oder Process zusammen, wie viele L4 darunter fertig sind.
  $: listOfCoverage = getLevelCoverage(2 - selectedCoverageLevel, subtaskCounts);
  $: coveragePercent = subtaskCoverage.length
    ? Math.round((100 * subtasksWithEnough) / subtaskCoverage.length)
    : 0;

  $: teamOverview = getTeamOverview(teams, recordingsForTeamOverview);

  function toggleTeamFilter(teamid) {
    selectedTeam = String(selectedTeam) === String(teamid) ? "" : teamid;
  }

  $: tableRows = (() => {
    if (!listOfSubtasks || !filteredListofRecordings) return [];
    // Teams aus "Set Up Teams", damit Zeilen ohne Registrierung ihr Team zeigen.
    const assignedTeamNames = (subtaskId) =>
      filterTeamOptions
        .filter((team) => team.subtaskIds?.has(String(subtaskId)))
        .map((team) => team.name);
    const UNASSIGNED_KEY = "__unassigned__";
    const recordingsById = new Map();
    (filteredListofRecordings || []).forEach((recording) => {
      if (!recording?.recorderid || recording.deleted) return;
      recordingsById.set(recording.recorderid, recording);
    });
    const uniqueRecordings = [...recordingsById.values()];
    const rowMap = new Map();
    uniqueRecordings.forEach((recording) => {
      const subtask = listOfSubtasks.find((st) =>
        st.recordings?.some((r) => r.recording_id === recording.recorderid),
      );
      let task = null;
      let subprocess = null;
      let process = null;
      if (subtask) {
        task = listOfTasks.find((t) => t.id === subtask.task_id);
        if (task) {
          subprocess = listOfSubprocesses.find(
            (sp) => sp.id === task.subprocess_id,
          );
          if (subprocess) {
            process = processes.find((p) => p.id === subprocess.process_id);
          }
        }
      }
      const key = subtask?.id ?? UNASSIGNED_KEY;
      const recordingDate = recording.endtimeutc
        ? new Date(recording.endtimeutc)
        : null;
      if (rowMap.has(key)) {
        const existing = rowMap.get(key);
        existing.count++;
        if (
          recordingDate &&
          !isNaN(recordingDate.getTime()) &&
          (!existing.newestDate || recordingDate > existing.newestDate)
        ) {
          existing.newestDate = recordingDate;
        }
        if (recording.teamid) existing.teamIds.add(recording.teamid);
        if (recording.team) existing.teamNames.add(recording.team);
      } else {
        const teamIds = new Set();
        if (recording.teamid) teamIds.add(recording.teamid);
        rowMap.set(key, {
          id: key,
          // Alle Teams, die diesen Subtask registriert haben, nicht nur das erste.
          teamNames: new Set(recording.team ? [recording.team] : []),
          l1: process?.name ?? "",
          l2: subprocess?.name ?? "",
          l3: task?.name ?? "",
          l4: subtask?.name ?? "",
          count: 1,
          newestDate:
            recordingDate && !isNaN(recordingDate.getTime())
              ? recordingDate
              : null,
          processid: process?.id ?? null,
          subprocessid: subprocess?.id ?? null,
          taskid: task?.id ?? null,
          subtaskid: subtask?.id ?? null,
          teamIds,
        });
      }
    });

    const filteredSubtasksForTable = getFilteredItemsAtLevel(3);
    filteredSubtasksForTable.forEach((subtask) => {
      if (rowMap.has(subtask.id)) return; // already has recordings
      const task = listOfTasks?.find((t) => t.id === subtask.task_id) ?? null;
      const subprocess = task
        ? (listOfSubprocesses?.find((sp) => sp.id === task.subprocess_id) ??
          null)
        : null;
      const process = subprocess
        ? (processes?.find((p) => p.id === subprocess.process_id) ?? null)
        : null;
      rowMap.set(subtask.id, {
        id: subtask.id,
        teamNames: new Set(assignedTeamNames(subtask.id)),
        l1: process?.name ?? "",
        l2: subprocess?.name ?? "",
        l3: task?.name ?? "",
        l4: subtask?.name ?? "",
        count: 0,
        newestDate: null,
        processid: process?.id ?? null,
        subprocessid: subprocess?.id ?? null,
        taskid: task?.id ?? null,
        subtaskid: subtask?.id ?? null,
        teamIds: new Set(),
      });
    });

    const assignedRows = Array.from(rowMap.values()).filter(
      (row) => row.id !== UNASSIGNED_KEY,
    );

    const filteredAssigned = assignedRows
      .filter((row) => {
        if (selectedAssignedOption === "enough_registrations") {
          return row.count >= sufficientNumberOfRegistrations;
        }
        if (selectedAssignedOption === "limited_registrations") {
          return row.count > 0 && row.count < sufficientNumberOfRegistrations;
        }
        if (selectedAssignedOption === "no_registrations")
          return row.count === 0;
        return true;
      })
      .map((row) => ({
        id: row.id,
        team: [...row.teamNames].sort((a, b) => a.localeCompare(b)).join(", "),
        l1: row.l1,
        l2: row.l2,
        l3: row.l3,
        l4: row.l4,
        nRecordings: row.count,
        newestRecording: row.newestDate ? formatNewestDate(row.newestDate) : "",
      }));

    return filteredAssigned;
  })();

  $: filteredTableRows = (
    tableSearchTerm
      ? tableRows.filter((row) =>
          [row.team, row.l1, row.l2, row.l3, row.l4, row.newestRecording].some((v) =>
            v?.toLowerCase().includes(tableSearchTerm.toLowerCase()),
          ),
        )
      : tableRows
  )
    .slice()
    // Lücken zuerst, damit fehlende Registrierungen oben stehen.
    .sort((a, b) => a.nRecordings - b.nRecordings);

  $: sortCoverageByName(coverageSorterByName, listOfCoverage);
  $: sortCoverageByNumber(coverageSorterByNumber, listOfCoverage);
  $: sortTeamByName(teamSorterByName, teamOverview);
  $: sortTeamByNumber(teamSorterByNumber, teamOverview);

  $: setWeeklyChartData(filteredListofRecordings, selectMode);
  $: trendWeeks = weeklyChartData?.[0]?.data?.length ?? 0;
  // Ein Trend braucht mindestens zwei Wochen. Hat das ganze Projekt weniger,
  // ersetzt der Empty State die Ansicht samt Filtern, weil Filtern nichts ändert.
  $: projectTrendWeeks = new Set(
    (listOfRecordings || [])
      .filter((rec) => rec.recorderid && !rec.deleted && rec.starttimeutc)
      .map((rec) => getMondayISO(rec.starttimeutc)),
  ).size;
  $: trendEmpty = selectMode === 1 && !loadingData && projectTrendWeeks < 2;

  // Grüner Anteil als Fortschrittsbalken im Hintergrund der Kachel
  function progressFill(percent) {
    return [
      { color: "#8ef095", percent },
      { color: "var(--pax-smoke)", percent: 100 - percent },
    ];
  }

  function sortCoverageByName(coverageSorterByName, listOfCoverage) {
    if (!coverageSorterByName || !listOfCoverage) return;
    displayedListOfCoverage = [...listOfCoverage].sort((a, b) => {
      if (coverageSorterByName === "asc") {
        return a.name.localeCompare(b.name);
      } else if (coverageSorterByName === "desc") {
        return b.name.localeCompare(a.name);
      } else {
        return 0;
      }
    });
  }

  function sortCoverageByNumber(coverageSorterByNumber, listOfCoverage) {
    if (!coverageSorterByNumber || !listOfCoverage) return;
    displayedListOfCoverage = [...listOfCoverage].sort((a, b) => {
      // Nach Anteil, bei Gleichstand das größere Element zuerst
      if (coverageSorterByNumber === "asc") {
        return a.percent - b.percent || b.total - a.total;
      } else if (coverageSorterByNumber === "desc") {
        return b.percent - a.percent || b.total - a.total;
      } else {
        return 0;
      }
    });
  }

  function sortTeamByNumber(teamSorterByNumber, teamOverview) {
    if (!teamSorterByNumber || !teamOverview) return;

    displayedTeamOverview = [...teamOverview].sort((a, b) => {
      if (teamSorterByNumber === "asc") {
        return a.registerscount - b.registerscount;
      } else if (teamSorterByNumber === "desc") {
        return b.registerscount - a.registerscount;
      } else {
        return 0;
      }
    });
  }

  function sortTeamByName(teamSorterByName, teamOverview) {
    if (!teamSorterByName || !teamOverview) return;
    displayedTeamOverview = [...teamOverview].sort((a, b) => {
      if (teamSorterByName === "asc") {
        return a.name.localeCompare(b.name);
      } else if (teamSorterByName === "desc") {
        return b.name.localeCompare(a.name);
      } else {
        return 0;
      }
    });
  }

  async function getTeamInfo(idproject) {
    try {
      const res = await fetchWrapper(
        `/api/damprojects/dam-teams?idproject=${idproject}`,
        { method: "GET" },
      );
      const data = await res.json();
      teams = data.map((team) => ({
        ...team,
        damUsers: team.damUsers.map((user) => user.id),
        subprocesses: team.subprocesses.map((subprocess) => subprocess.id),
      }));
      return teams;
    } catch (err) {
      console.error(err);
    }
  }

  function getDataInflowKPIs() {
    const recordingsById = new Map();
    (filteredListofRecordings || []).forEach((recording) => {
      if (!recording?.recorderid || recording.deleted) return;
      recordingsById.set(recording.recorderid, recording);
    });
    const uniqueRecordings = [...recordingsById.values()];

    totalRegistrations = uniqueRecordings.length;

    const now = new Date();
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(now.getDate() - 7);
    last7DaysRegistrations = uniqueRecordings.filter((rec) => {
      const recDate = new Date(rec.endtimeutc);
      return recDate >= sevenDaysAgo && recDate <= now;
    }).length;

    const newest = uniqueRecordings
      .map((rec) => new Date(rec.endtimeutc))
      .filter((date) => !isNaN(date.getTime()))
      .sort((a, b) => b - a)[0];
    lastRegistration = newest ? formatNewestDate(newest) : "None";

    unassignedRegistrations = uniqueRecordings.filter((rec) => {
      const subtask = listOfSubtasks.find((t) =>
        t.recordings?.some((r) => r.recording_id === rec.recorderid),
      );
      return !subtask;
    }).length;

  }

  function getFilteredItemsAtLevel(level) {
    // Step 1: Apply each direct filter independently
    let filteredSubtasks = listOfSubtasks;
    if (selectedSubtask && selectedSubtask !== "Subtask") {
      filteredSubtasks = filteredSubtasks.filter(
        (st) => st.id === selectedSubtask,
      );
    }

    // Filter subtasks by team: keep subtasks assigned to the team in "Set Up Teams"
    // and subtasks that have recordings from the selected team
    if (selectedTeam && selectedTeam !== "Team") {
      const teamRecorderIds = new Set(
        (filteredListofRecordings || [])
          .filter((r) => r.teamid === selectedTeam)
          .map((r) => r.recorderid),
      );
      const teamSubtaskIds =
        filterTeamOptions.find(
          (team) => String(team.id) === String(selectedTeam),
        )?.subtaskIds || new Set();
      filteredSubtasks = filteredSubtasks.filter(
        (st) =>
          teamSubtaskIds.has(String(st.id)) ||
          st.recordings?.some((r) => teamRecorderIds.has(r.recording_id)),
      );
    }

    let filteredTasks = listOfTasks;
    if (selectedTask && selectedTask !== "Task") {
      filteredTasks = filteredTasks.filter((t) => t.id === selectedTask);
    }

    let filteredSubprocesses = listOfSubprocesses;
    if (selectedSubprocess && selectedSubprocess !== "Subprocess") {
      filteredSubprocesses = filteredSubprocesses.filter(
        (sp) => sp.id === selectedSubprocess,
      );
    }

    let filteredProcesses = processes;
    if (selectedProcess && selectedProcess !== "Process") {
      filteredProcesses = filteredProcesses.filter(
        (p) => p.id === selectedProcess,
      );
    }

    // Step 2: Cascade top-down (parent restricts children)
    filteredSubprocesses = filteredSubprocesses.filter((sp) =>
      filteredProcesses.some((p) => p.id === sp.process_id),
    );
    filteredTasks = filteredTasks.filter((t) =>
      filteredSubprocesses.some((sp) => sp.id === t.subprocess_id),
    );
    filteredSubtasks = filteredSubtasks.filter((st) =>
      filteredTasks.some((t) => t.id === st.task_id),
    );

    // Step 3: Cascade bottom-up (children restrict parents)
    filteredTasks = filteredTasks.filter((t) =>
      filteredSubtasks.some((st) => st.task_id === t.id),
    );
    filteredSubprocesses = filteredSubprocesses.filter((sp) =>
      filteredTasks.some((t) => t.subprocess_id === sp.id),
    );
    filteredProcesses = filteredProcesses.filter((p) =>
      filteredSubprocesses.some((sp) => sp.process_id === p.id),
    );

    if (level === 0) return filteredProcesses;
    if (level === 1) return filteredSubprocesses;
    if (level === 2) return filteredTasks;
    return filteredSubtasks;
  }

  function getSubtaskCounts() {
    const counts = new Map(); // subtaskId → { name, task_id, count }
    if (!filteredListofRecordings || !listOfSubtasks) return counts;
    getFilteredItemsAtLevel(3).forEach((subtask) =>
      counts.set(subtask.id, {
        name: subtask.name,
        task_id: subtask.task_id,
        count: 0,
      }),
    );
    const subtaskIdByRecording = new Map();
    listOfSubtasks.forEach((subtask) =>
      (subtask.recordings || []).forEach((recording) =>
        subtaskIdByRecording.set(recording.recording_id, subtask.id),
      ),
    );
    const counted = new Set();
    filteredListofRecordings.forEach((recording) => {
      if (!recording?.recorderid || recording.deleted) return;
      if (counted.has(recording.recorderid)) return;
      counted.add(recording.recorderid);
      const entry = counts.get(subtaskIdByRecording.get(recording.recorderid));
      if (entry) entry.count++;
    });
    return counts;
  }

  // level 0 = Process, 1 = Subprocess, 2 = Task
  function getLevelCoverage(level, counts) {
    const taskById = new Map((listOfTasks || []).map((t) => [t.id, t]));
    const subprocessById = new Map(
      (listOfSubprocesses || []).map((sp) => [sp.id, sp]),
    );
    const elements = new Map(
      getFilteredItemsAtLevel(level).map((item) => [
        item.id,
        { name: item.name, done: 0, total: 0, percent: 0 },
      ]),
    );
    counts.forEach((subtask) => {
      const task = taskById.get(subtask.task_id);
      const subprocess = subprocessById.get(task?.subprocess_id);
      const ancestorId = [subprocess?.process_id, subprocess?.id, task?.id][
        level
      ];
      const element = elements.get(ancestorId);
      if (!element) return;
      element.total++;
      if (subtask.count >= sufficientNumberOfRegistrations) element.done++;
    });
    return Array.from(elements.values())
      .filter((element) => element.total > 0)
      .map((element) => ({
        ...element,
        percent: Math.round((100 * element.done) / element.total),
      }));
  }

  // Registrierungen je Team. Pro Mitglied geht nicht, weil Teams oft ohne
  // hinterlegte Mitglieder registrieren und dann 0 zeigen würden.
  function getTeamOverview(teams, filteredListofRecordings) {
    if (!teams?.length || !filteredListofRecordings) return [];

    const teamNames = new Map(teams.map((team) => [team.id, team.name]));

    const teamMap = new Map(
      teams.map((team) => [
        team.id,
        { name: team.name, count: 0, teamid: team.id },
      ]),
    );
    filteredListofRecordings.forEach((recording) => {
      if (!recording?.recorderid || recording.deleted) return;
      const teamid = recording.teamid;
      if (!teamid) return;

      if (teamMap.has(teamid)) {
        teamMap.get(teamid).count++;
      } else {
        teamMap.set(teamid, {
          name: teamNames.get(teamid) ?? recording.team ?? "Unknown",
          count: 1,
          teamid,
        });
      }
    });
    return Array.from(teamMap.values()).map((team) => ({
      name: team.name,
      teamid: team.teamid,
      registerscount: team.count,
    }));
  }

  function getMondayISO(dateStr) {
    const d = new Date(dateStr);
    const day = d.getUTCDay(); // 0=Sun, 1=Mon, ..6=Sat
    const diff = day === 0 ? -6 : 1 - day;
    d.setUTCDate(d.getUTCDate() + diff);
    return d.toISOString().slice(0, 10);
  }

  function setWeeklyChartData() {
    if (
      !filteredListofRecordings ||
      filteredListofRecordings.length === 0 ||
      loadedDataBeforeChart ||
      selectMode !== 1
    ) {
      weeklyChartData = [];
      // ohne Registrierungen sonst endloser Spinner (loadingChart startet mit true)
      if (!loadedDataBeforeChart) loadingChart = false;
      return;
    }
    loadingChart = true;
    const weekMap = new Map();
    filteredListofRecordings.forEach((rec) => {
      if (!rec?.recorderid || rec.deleted) return;
      const monday = getMondayISO(rec.starttimeutc);
      weekMap.set(monday, (weekMap.get(monday) || 0) + 1);
    });
    const sorted = [...weekMap.entries()].sort(([a], [b]) =>
      a.localeCompare(b),
    );
    weeklyChartData = [
      {
        label: "Registrations",
        data: sorted.map(([x, y]) => ({ x, y })),
      },
    ];
    loadingChart = false;
  }

  function setFilteredListofRecordings({ ignoreTeam = false } = {}) {
    return (
      listOfRecordings &&
      listOfRecordings.filter((rec) => {
        const subtask = listOfSubtasks.find((st) =>
          st.recordings?.some((r) => r.recording_id === rec.recorderid),
        );

        let task = null;
        let subprocess = null;
        let process = null;

        if (subtask) {
          task = listOfTasks.find((t) => t.id === subtask.task_id);
          if (task) {
            subprocess = listOfSubprocesses.find(
              (sp) => sp.id === task.subprocess_id,
            );
            if (subprocess) {
              process = processes.find((p) => p.id === subprocess.process_id);
            }
          }
        }

        const matchProcess =
          !selectedProcess ||
          selectedProcess === "Process" ||
          process?.id === selectedProcess;
        const matchSubprocess =
          !selectedSubprocess ||
          selectedSubprocess === "Subprocess" ||
          subprocess?.id === selectedSubprocess;
        const matchTask =
          !selectedTask || selectedTask === "Task" || task?.id === selectedTask;
        const matchSubtask =
          !selectedSubtask ||
          selectedSubtask === "Subtask" ||
          subtask?.id === selectedSubtask;
        const matchTeam =
          ignoreTeam ||
          !selectedTeam ||
          selectedTeam === "Team" ||
          rec.teamid === selectedTeam;

        return (
          matchProcess &&
          matchSubprocess &&
          matchTask &&
          matchSubtask &&
          matchTeam
        );
      })
    );
  }

  onMount(async () => {
    await getTeamInfo(idproject);
    loadedDataBeforeChart = false;
    setWeeklyChartData();
  });
</script>

<Header
  section="Project Progress"
  area="Discovery"
  breadcrumb="Registration Dashboard"
  tooltipText="Monitor task registration progress, coverage, and activity across teams and registered processes."
  {breadcrumbsClickable}
>
  <div class="dashboard-switcher">
    <ContentSwitcher
      bind:selected={selectMode}
      options={["Table", "Trend"]}
      fitContent={true}
    ></ContentSwitcher>
  </div>
  <ProjectStepActions
    {idproject}
    {step}
    {stage}
    showGoBackButton={false}
    showMarkAsCompletedButton={false}
    showCompletedState={false}
    text="Complete Registration Phase"
    completionWarning="This marks the task registration phase as complete. Please make sure enough registrations have been collected before continuing. This action cannot be undone."
  />
</Header>

<div class:table_section={selectMode === 0}>
  <SectionWrapper
    fitScreen={selectMode === 1}
    seamless={true}
    seamlessPadding={true}
    emptyState={trendEmpty}
  >
    <div slot="emptyState">
      <EmptyStateText
        title={projectTrendWeeks === 0
          ? "No registrations yet"
          : "Not enough data for a trend yet"}
        hint={projectTrendWeeks === 0
          ? "The trend shows registrations per week. It appears once your teams have registered tasks in at least two different weeks."
          : "The trend shows registrations per week. So far all registrations are from the same week. The trend appears once there are registrations from a second week."}
      />
    </div>
    <div slot="main" class="main_wrapper">
      <InfoBlock
        type="info"
        heading="How to read this dashboard"
        message={`See how many registrations each subtask (L4) has. Each subtask needs at least ${sufficientNumberOfRegistrations}. Filter by Registration status to find subtasks that still need examples.`}
      />
      <div
        class="select_wrapper"
        data-guide="dashboard-filters"
      >
        <div class="filter_wrapper">
          <LFilters
            bind:this={lFiltersRef}
            {idproject}
            showAssignedFilter={true}
            showL4={false}
            {selectAssignedOptions}
            bind:selectedProcess
            bind:selectedSubprocess
            bind:selectedTask
            bind:selectedSubtask
            bind:selectedTeam
            bind:selectedAssignedOption
            bind:teamOptions={filterTeamOptions}
          >
            <svelte:fragment slot="before-filters">
              {#if selectMode === 0}
                <div class="filter_field dashboard_search">
                  <span class="search_label">Search</span>
                  <Search
                    size="lg"
                    bind:value={tableSearchTerm}
                    labelText="Search registrations"
                    placeholder="Search subtasks or teams"
                  />
                </div>
              {/if}
            </svelte:fragment>
          </LFilters>
        </div>
      </div>
      <div class="canvas" class:table_canvas={selectMode === 0}>
        <div class="ui_viewer_wrapper" class:table_ui_viewer={selectMode === 0}>
          {#if loadingData}
            <div class="loading_wrapper">
              <Loading withOverlay={false} />
            </div>
          {:else if selectMode === 0}
            <div class="table_view_wrapper" data-guide="dashboard-table">
              {#if filteredTableRows.length === 0}
                <div class="table_empty">
                  {#if hasActiveFilters}
                    <EmptyStateText
                      title="No subtasks match these filters"
                      hint="Change or reset the filters to see more subtasks."
                    >
                      <svelte:fragment slot="actions">
                        <Button
                          kind="secondary"
                          size="field"
                          text="Reset filters"
                          on:click={resetAllFilters}
                        />
                      </svelte:fragment>
                    </EmptyStateText>
                  {:else}
                    <EmptyStateText
                      title="No subtasks yet"
                      hint="Subtasks appear here once the project framework is set up."
                    />
                  {/if}
                </div>
              {:else}
              <DataTableBlack
                headers={tableHeaders}
                rows={filteredTableRows}
                sortable
                slicePage
                bind:page={tablePagination.page}
                bind:pageSize={tablePagination.pageSize}
                pageSizes={[10, 25, 50, 75, 100]}
                showNewButton={false}
                showToolbar={false}
                totalItems={filteredTableRows.length}
              >
                <svelte:fragment slot="cell-header" let:header>
                  {#if header.key === "nRecordings"}
                    <span class="header_with_legend">
                      {header.value}
                      <Tooltip direction="bottom" align="end">
                        <span class="tooltip_legend">
                          {#each countLegend as entry}
                            <span class="legend_item">
                              <span class="legend_swatch {entry.className}"></span>
                              <span>{entry.label}</span>
                            </span>
                          {/each}
                        </span>
                      </Tooltip>
                    </span>
                  {:else}
                    {header.value}
                  {/if}
                </svelte:fragment>
                <svelte:fragment slot="cell" let:cell>
                  {#if cell.key === "nRecordings"}
                    <span
                      class="count_pill"
                      class:count_none={cell.value === 0}
                      class:count_limited={cell.value > 0 &&
                        cell.value < sufficientNumberOfRegistrations}
                      class:count_enough={cell.value >=
                        sufficientNumberOfRegistrations}
                    >
                      {cell.value}
                    </span>
                  {:else}
                    <div
                      class="cell_content"
                      class:newest_cell={cell.key === "newestRecording"}
                      title={String(cell.value ?? "")}
                    >
                      {cell.value ?? ""}
                    </div>
                  {/if}
                </svelte:fragment>
              </DataTableBlack>
              {/if}
            </div>
          {:else if loadingChart === false && trendWeeks < 2}
            <!-- Das Projekt hat genug Wochen, nur die Filter lassen zu wenig übrig. Filter bleiben deshalb sichtbar. -->
            <div class="trend_empty">
              <EmptyStateText
                title="No trend for these filters"
                hint="The matching registrations are all from the same week. Change the filters to see a trend."
              />
            </div>
          {:else if loadingChart === false}
            <LineChartNeu
              height="100%"
              width="100%"
              variant="clean"
              axes={["y"]}
              dataSets={weeklyChartData}
              displayLegend={false}
              noDecimals={true}
            />
          {:else}
            <div class="loading_wrapper">
              <Loading withOverlay={false} />
            </div>
          {/if}
        </div>
      </div>
    </div>

    <div slot="menu" class="menu">
      <div data-guide="dashboard-inflow">
        <Accordion
          heading="Progress"
          bind:open={dataInflowOpen}
          seamless={true}
          seamlessPadding={true}
        >
          <div class="dataInflow_wrapper">
            <!-- Anzahl und Anteil in einer Kachel, der grüne Hintergrund zeigt den Fortschritt -->
            <SideKPI
              name={`L4 with ${sufficientNumberOfRegistrations}+ registrations`}
              value={`${subtasksWithEnough} of ${subtaskCoverage.length} (${coveragePercent}%)`}
              loading={loadingData}
              multicolor={progressFill(coveragePercent)}
              tooltipText={`Subtasks (L4) that reached the goal of ${sufficientNumberOfRegistrations} registrations. Team, process, subprocess and task filters apply. Registration status and search only filter the table.`}
              tooltipDirection="left"
            ></SideKPI>
            <SideKPI
              name="Total Registrations"
              value={totalRegistrations || 0}
              loading={loadingData}
            ></SideKPI>
            <SideKPI
              name="Last 7 Days Registrations"
              value={last7DaysRegistrations || 0}
              loading={loadingData}
            ></SideKPI>
            <SideKPI
              name="Unassigned Registrations"
              value={unassignedRegistrations || 0}
              loading={loadingData}
              tooltipText="Registrations that are not linked to a subtask yet. They do not count toward any subtask in the table."
              tooltipDirection="left"
            ></SideKPI>
            <SideKPI
              name="Last Registration"
              value={lastRegistration}
              loading={loadingData}
            ></SideKPI>
          </div>
        </Accordion>
      </div>
      <div data-guide="dashboard-teams">
        <Accordion
          bind:open={teamOverviewOpen}
          seamless={true}
          seamlessPadding={true}
        >
          <svelte:fragment slot="heading">
            <span class="accordion-heading-with-tooltip">
              Team Overview
              <Tooltip>
                <p>
                  This statistic shows how many registrations each team has
                  made.
                </p>
                <p>Click a team to show only its subtasks in the table. Click it again to show all teams.</p>
                <p>
                  <em>The values depend on the filters at the top of this page.</em>
                </p>
              </Tooltip>
            </span>
          </svelte:fragment>
          <SearchableList
            items={displayedTeamOverview}
            placeholder="Search for Team"
            loading={loadingData}
          >
            <svelte:fragment slot="middle">
              <div class="shorter-button-wrapper">
                <div
                  class:sorter-button={true}
                  class:sorter-desc={teamSorterByName === "desc"}
                  class:sorter-asc={teamSorterByName === "asc"}
                >
                  <Button
                    text="Sort by name"
                    icon={teamSorterByName ? SORTED_ARROW2_ICON : SORT_ARROW_ICON}
                    iconRight
                    size="small"
                    large
                    kind="secondary"
                    on:click={() => {
                      teamSorterByNumber = "";
                      teamSorterByName =
                        !teamSorterByName || teamSorterByName === "desc"
                          ? "asc"
                          : "desc";
                    }}
                 />
                </div>
                <div
                  class:sorter-button={true}
                  class:sorter-desc={teamSorterByNumber === "desc"}
                  class:sorter-asc={teamSorterByNumber === "asc"}
                >
                  <Button
                    text="Sort by number"
                    icon={teamSorterByNumber ? SORTED_ARROW2_ICON : SORT_ARROW_ICON}
                    iconRight
                    size="small"
                    large
                    kind="secondary"
                    on:click={() => {
                      teamSorterByName = "";
                      teamSorterByNumber =
                        !teamSorterByNumber || teamSorterByNumber === "desc"
                          ? "asc"
                          : "desc";
                    }}
                 />
                </div>
              </div>
            </svelte:fragment>
            <svelte:fragment slot="loading">
              <SideKPI name="" value="" loading={true}></SideKPI>
            </svelte:fragment>
            <svelte:fragment let:item>
              <button
                type="button"
                class="team_filter_button"
                class:team_filter_active={String(selectedTeam) === String(item.teamid)}
                aria-pressed={String(selectedTeam) === String(item.teamid)}
                title={String(selectedTeam) === String(item.teamid)
                  ? "Show all teams"
                  : "Show only this team"}
                on:click={() => toggleTeamFilter(item.teamid)}
              >
                <SideKPI name={item.name} value={item.registerscount || 0}></SideKPI>
              </button>
            </svelte:fragment>
          </SearchableList>
        </Accordion>
      </div>

      <div data-guide="dashboard-coverage">
        <Accordion
          bind:open={coverageOpen}
          seamless={true}
          seamlessPadding={true}
        >
          <svelte:fragment slot="heading">
            <span class="accordion-heading-with-tooltip">
              Coverage
              <Tooltip>
                <p>
                  Shows for each task, subprocess or process how many of its
                  subtasks (L4) have {sufficientNumberOfRegistrations} or more
                  registrations. The green bar shows the share.
                </p>
                <p>The weakest areas are listed first.</p>
                <p>
                  <em>The values depend on the filters at the top of this page.</em>
                </p>
              </Tooltip>
            </span>
          </svelte:fragment>
          <SearchableList
            items={displayedListOfCoverage}
            placeholder="Search"
            loading={loadingData}
          >
            <svelte:fragment slot="middle">
              <div class="switcher_kpi_wrapper">
                <ContentSwitcher
                  bind:selected={selectedCoverageLevel}
                  options={levelNames}
                ></ContentSwitcher>
              </div>
              <div class="shorter-button-wrapper">
                <div
                  class:sorter-button={true}
                  class:sorter-desc={coverageSorterByName === "desc"}
                  class:sorter-asc={coverageSorterByName === "asc"}
                >
                  <Button
                    text="Sort by name"
                    icon={coverageSorterByName ? SORTED_ARROW2_ICON : SORT_ARROW_ICON}
                    iconRight
                    large
                    size="small"
                    kind="secondary"
                    on:click={() => {
                      coverageSorterByNumber = "";
                      coverageSorterByName =
                        !coverageSorterByName || coverageSorterByName === "desc"
                          ? "asc"
                          : "desc";
                    }}
                  />
                </div>
                <div
                  class:sorter-button={true}
                  class:sorter-desc={coverageSorterByNumber === "desc"}
                  class:sorter-asc={coverageSorterByNumber === "asc"}
                >
                  <Button
                    text="Sort by number"
                    icon={coverageSorterByNumber
                      ? SORTED_ARROW2_ICON
                      : SORT_ARROW_ICON}
                    iconRight
                    size="small"
                    large
                    kind="secondary"
                    on:click={() => {
                      coverageSorterByName = "";
                      coverageSorterByNumber =
                        !coverageSorterByNumber ||
                        coverageSorterByNumber === "desc"
                          ? "asc"
                          : "desc";
                    }}
                 />
                </div>
              </div>
            </svelte:fragment>
            <svelte:fragment slot="loading">
              <SideKPI name="" value="" loading={true}></SideKPI>
            </svelte:fragment>
            <svelte:fragment let:item>
              <SideKPI
                name={item.name}
                value={`${item.done} of ${item.total} L4`}
                multicolor={progressFill(item.percent)}
              />
            </svelte:fragment>
          </SearchableList>
        </Accordion>
      </div>
    </div>
  </SectionWrapper>
</div>

<SpotlightGuide
  bind:open={showDashboardGuide}
  bind:currentStep={dashboardGuideStep}
  steps={dashboardGuideSteps}
  allowInteraction={false}
/>

<style>
  .select_wrapper {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 0.25rem;
    width: 100%;
  }

  .filter_wrapper {
    display: flex;
    flex-direction: row;
    gap: 0.38rem;
    width: 100%;
    background: var(--pax-white);
    border-radius: 0.5rem;
    padding: 0;
  }

  .filter_wrapper :global(.filter_field) {
    width: 10.5rem;
    min-width: 0;
  }

  .filter_wrapper > :global(.filter_wrapper) {
    width: 100%;
    min-width: 0;
  }

  .filter_wrapper .dashboard_search {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    flex: 1 0 10.5rem;
    width: auto;
  }

  .dashboard_search :global(.bx--search-input) {
    height: 2.5rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.5rem;
    background-color: var(--pax-white);
    color: var(--pax-black);
  }

  .dashboard_search :global(.bx--search-input::placeholder) {
    color: var(--pax-grey);
  }

  .dashboard_search :global(.bx--search-magnifier-icon),
  .dashboard_search :global(.bx--search-close) {
    fill: var(--pax-black);
  }

  .search_label {
    color: var(--pax-black);
    font-size: 0.75rem;
    line-height: 1rem;
  }

  .filter_wrapper :global(.filter_field .custom_select),
  .filter_wrapper :global(.filter_field .bx--form-item),
  .filter_wrapper :global(.filter_field .bx--select-input),
  .filter_wrapper :global(.filter_field .bx--select-input__wrapper) {
    width: 100%;
  }

  .table_section {
    width: 100%;
  }

  .table_section :global(.collapsible_wrapper),
  .table_section :global(.view_wrapper),
  .table_section :global(.view_wrapper > .canvas) {
    height: auto;
    min-height: auto;
  }

  .table_section :global(.view_wrapper > .canvas) {
    align-self: flex-start;
  }

  .canvas {
    flex: 1;
    min-height: 0;
    width: 100%;
    z-index: 0; /* Platziert das Canvas hinter den Overlays */
    display: flex;
    justify-content: flex-start;
    align-items: stretch;
  }

  .table_canvas {
    flex: 0 0 auto;
    min-height: auto;
    padding: 0;
  }

  .menu {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .main_wrapper {
    position: relative;
    flex: 1;
    height: 100%;
    width: 100%;
    min-width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 0.5rem;
    min-height: 0;
  }

  .main_wrapper :global(.warning-notice) {
    margin-bottom: 0.5rem;
  }

  .ui_viewer_wrapper {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    width: 100%;
  }

  .table_ui_viewer {
    flex: 0 0 auto;
    min-height: auto;
  }

  /* Outline nach innen statt Rahmen, damit der Abstand genau --control-gap bleibt */
  .team_filter_button {
    display: block;
    width: 100%;
    padding: 0;
    border: none;
    border-radius: 0.5rem;
    outline: 2px solid transparent;
    outline-offset: -2px;
    background: none;
    text-align: left;
    cursor: pointer;
  }

  .team_filter_button:hover,
  .team_filter_button:focus-visible {
    outline-color: var(--pax-grey);
  }

  .team_filter_active {
    outline-color: var(--pax-black);
  }

  /* Platz unter dem Text, weil EmptyStateText die Buttons absolut darunter setzt */
  .table_empty {
    padding: 2rem 0 5rem;
    display: flex;
    justify-content: center;
  }

  .trend_empty {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
  }

  .loading_wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
  }

  .switcher_kpi_wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--control-gap);
    width: 100%;
  }

  .switcher_kpi_wrapper :global(.grid_container) {
    width: 100%;
    height: 3rem !important;
    min-height: 3rem;
    max-height: 3rem;
  }

  .dataInflow_wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .table_view_wrapper {
    flex: 0 0 auto;
    min-height: auto;
    width: 100%;
    display: flex;
    flex-direction: column;
    /* overflow-y: auto; */
  }

  .shorter-button-wrapper {
    display: flex;
    gap: var(--control-gap);
  }

  :global(.sorter-asc img) {
    transform: rotate(180deg);
    transform-origin: center;
  }

  .sorter-button {
    flex: 1 1 0;
    min-width: 0;
  }

  /* Beide Sortier-Buttons einzeilig und so hoch wie die übrigen Controls */
  .sorter-button :global(.mainbutton),
  .sorter-button :global(button) {
    width: 100%;
    height: var(--control-height) !important;
    min-height: var(--control-height);
    white-space: nowrap;
  }

  /* Gleiche Farben wie Coverage (SideKPI colorByValue) */
  .count_pill {
    display: inline-block;
    min-width: 2rem;
    padding: 0.125rem 0.5rem;
    border-radius: 0.25rem;
    text-align: center;
    color: var(--pax-black);
  }

  .header_with_legend {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
  }

  .tooltip_legend {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .legend_item {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
  }

  .legend_swatch {
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 0.125rem;
  }

  .count_none {
    background: #f08e90;
  }

  .count_limited {
    background: #e9f08e;
  }

  .count_enough {
    background: #8ef095;
  }

  .accordion-heading-with-tooltip {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
  }

  /* The wrapper takes the line height of the heading, the icon would stick to its top */
  .accordion-heading-with-tooltip :global(.bx--tooltip-wrapper) {
    display: flex;
  }
</style>
